import XDate from 'xdate';
import { Col, P, Row, Stack } from '@workramp/ui';
import { I18n } from '~/common/explicit-exports';
import { academyCustomCSS } from '~/constants';
import { getExpiryData } from '~/components/academies/common/date-utils';
import { AutoResizingText } from './AutoResizingText';
import {
  StyledWrapper,
  StyledBackgroundImg,
  StyledSidebar,
  StyledBadgeImg,
  StyledSidebarCertificateTitleContainer,
  StyledSidebarCertificateTitle,
  StyledCertificateId,
  StyledLogo,
  StyledMain,
  StyledMainContent,
  StyledLearnerNameContainer,
  StyledLearnerName,
  StyledCompletedMsg,
  StyledMainCertificateTitle,
  StyledAwardedAtDate,
  StyledCertificateDescription,
  StyledCertFooterContainer,
} from './AcademyCertTemplate2.styles';

const { APP_CONSTANTS } = window;
const DEFAULT_BADGE_IMAGE_URL = `${APP_CONSTANTS.DEFAULT_BADGE_IMAGES.MEDAL.url}?forDisplay=1`;

export const AcademyCertTemplate2 = ({
  isModalVisible,
  certificateTitle,
  certificateDescription,
  certificateId,
  certificateLogo,
  certificateAwardedAt,
  certificateExpiresAt,
  showCertificateId,
  showCertificateDescription,
  issuerName,
  issuerTitle,
  useLogo,
  learnerName,
  backgroundImage,
  badgeImage,
  sidebarTextColor,
  mainTextColor,
}) => {
  const TRANSLATED_AWARDED_DATE = new XDate(certificateAwardedAt, true).toString('MMMM dd, yyyy');

  const TRANSLATED_SUCCESSFULLY_COMPLETED = I18n.t(
    'js.academy.certifications.awarded_certification.successfully_completed',
  );

  const TRANSLATED_EARNED_ON = I18n.t('js.academy.certifications.awarded_certification.earned_on', {
    date: TRANSLATED_AWARDED_DATE,
  });

  let TRANSLATED_EXPIRATION_TEXT = null;

  if (certificateExpiresAt) {
    const { translatedExpiresDate, translatedExpiresText } = getExpiryData(certificateAwardedAt, certificateExpiresAt, {
      useShortLabel: true,
    });
    TRANSLATED_EXPIRATION_TEXT = `${translatedExpiresText} ${translatedExpiresDate}`;
  }

  return (
    <StyledWrapper className={academyCustomCSS.certificateTemplate2.certWrapper}>
      {backgroundImage && (
        <StyledBackgroundImg
          className={academyCustomCSS.certificateTemplate2.background}
          src={backgroundImage}
          crossOrigin="anonymous"
          alt=""
        />
      )}
      <StyledSidebar $textColor={sidebarTextColor} className={academyCustomCSS.certificateTemplate2.sidebar}>
        <StyledBadgeImg
          src={badgeImage || DEFAULT_BADGE_IMAGE_URL}
          crossOrigin="anonymous"
          alt=""
          className={academyCustomCSS.certificateTemplate2.badge}
        />
        <StyledSidebarCertificateTitleContainer>
          <StyledSidebarCertificateTitle
            className={academyCustomCSS.certificateTemplate2.sideBarCertTitle}
            baseFontSize={0}
            shouldStartResizing={isModalVisible}
            maxHeight="64px"
          >
            {certificateTitle}
          </StyledSidebarCertificateTitle>
        </StyledSidebarCertificateTitleContainer>
        {showCertificateId && (
          <StyledCertificateId className={academyCustomCSS.certificateTemplate2.certId} size={-1}>
            ID: {certificateId}
          </StyledCertificateId>
        )}
        {useLogo && (
          <StyledLogo
            src={certificateLogo}
            crossOrigin="anonymous"
            className={academyCustomCSS.certificateTemplate2.logo}
          />
        )}
      </StyledSidebar>
      <StyledMain $textColor={mainTextColor} className={academyCustomCSS.certificateTemplate2.main}>
        <StyledMainContent>
          <StyledLearnerNameContainer>
            <StyledLearnerName
              className={academyCustomCSS.certificateTemplate2.learnerName}
              baseFontSize={7}
              shouldStartResizing={isModalVisible}
              maxHeight="40px"
              weight="bold"
            >
              {learnerName}
            </StyledLearnerName>
          </StyledLearnerNameContainer>
          <div className={academyCustomCSS.certificateTemplate2.completionInfo}>
            <StyledCompletedMsg className={academyCustomCSS.certificateTemplate2.completedMsg}>
              {TRANSLATED_SUCCESSFULLY_COMPLETED}
            </StyledCompletedMsg>
            <StyledMainCertificateTitle>
              <AutoResizingText
                className={academyCustomCSS.certificateTemplate2.mainCertTitle}
                baseFontSize={7}
                shouldStartResizing={isModalVisible}
                maxHeight="80px"
                weight="bold"
              >
                {certificateTitle}
              </AutoResizingText>
            </StyledMainCertificateTitle>
            <StyledAwardedAtDate className={academyCustomCSS.certificateTemplate2.awardedAtDate}>
              {TRANSLATED_EARNED_ON}
            </StyledAwardedAtDate>
          </div>

          {showCertificateDescription && (
            <StyledCertificateDescription
              className={academyCustomCSS.certificateTemplate2.description}
              size={0}
              maxLines={3}
              density="lg"
            >
              {certificateDescription}
            </StyledCertificateDescription>
          )}
          <StyledCertFooterContainer className={academyCustomCSS.certificateTemplate2.footerContainer}>
            <Stack size={2}>
              <Row gapX={0}>
                <Col>
                  <P size={0} className={academyCustomCSS.certificateTemplate2.issuerName}>
                    {issuerName}
                  </P>
                </Col>
                {certificateExpiresAt && (
                  <Col>
                    <P size={0} className={academyCustomCSS.certificateTemplate2.expiresAtDate}>
                      {TRANSLATED_EXPIRATION_TEXT}
                    </P>
                  </Col>
                )}
              </Row>
              <P size={0} className={academyCustomCSS.certificateTemplate2.issuerTitle}>
                {issuerTitle}
              </P>
            </Stack>
          </StyledCertFooterContainer>
        </StyledMainContent>
      </StyledMain>
    </StyledWrapper>
  );
};
